import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Button,
  ButtonBase,
  Checkbox,
  CircularProgress,
  ToggleButton,
  ToggleButtonGroup,
  Tooltip,
  Typography,
} from '@mui/material';
import { alpha, keyframes } from '@mui/material/styles';
import ChecklistIcon from '@mui/icons-material/Checklist';
import EventNoteOutlinedIcon from '@mui/icons-material/EventNoteOutlined';
import FactCheckOutlinedIcon from '@mui/icons-material/FactCheckOutlined';
import CakeOutlinedIcon from '@mui/icons-material/CakeOutlined';
import ReplayIcon from '@mui/icons-material/Replay';
import LinkIcon from '@mui/icons-material/Link';
import { orchard } from '../../theme';

/* «Обзор: что и когда» — my tasks, report deadlines, assessment / show days,
   birthdays, children waiting for a check, links that expire — grouped by
   overdue / today / tomorrow / this week / later (GET /api/tasks/review). */

const KIND = {
  task: { Icon: ChecklistIcon, color: '#7A4988', ru: 'Задача', en: 'Task' },
  deliverable: {
    Icon: EventNoteOutlinedIcon,
    color: orchard.cherry,
    ru: 'Сдать',
    en: 'Hand in',
  },
  check: {
    Icon: FactCheckOutlinedIcon,
    color: '#3b82f6',
    ru: 'По плану',
    en: 'Plan',
  },
  birthday: {
    Icon: CakeOutlinedIcon,
    color: '#ec4899',
    ru: 'День рождения',
    en: 'Birthday',
  },
  reassess: {
    Icon: ReplayIcon,
    color: '#D98E04',
    ru: 'Проверки',
    en: 'Checks',
  },
  link: { Icon: LinkIcon, color: orchard.leaf, ru: 'Ссылка', en: 'Link' },
};

const rise = keyframes`
  from { opacity: 0; transform: translateY(8px); }
  to { opacity: 1; transform: none; }
`;

const addDays = (key, n) => {
  const [y, m, d] = key.split('-').map(Number);
  return new Date(Date.UTC(y, m - 1, d + n)).toISOString().slice(0, 10);
};
const dateRu = (key) => {
  const [y, m, d] = key.split('-').map(Number);
  return new Date(Date.UTC(y, m - 1, d)).toLocaleDateString('ru-RU', {
    weekday: 'short',
    day: 'numeric',
    month: 'short',
    timeZone: 'UTC',
  });
};

export default function ReviewBoard({ en, onOpenBoard }) {
  const navigate = useNavigate();
  const [days, setDays] = useState(14);
  const [data, setData] = useState(null);
  const [error, setError] = useState('');
  const [done, setDone] = useState(new Set());

  const load = useCallback(async () => {
    setError('');
    try {
      const res = await fetch(`/api/tasks/review?days=${days}`, {
        credentials: 'include',
      });
      const j = await res.json();
      if (!res.ok) throw new Error(j.message || `HTTP ${res.status}`);
      setData(j);
    } catch (e) {
      setError(e.message);
    }
  }, [days]);
  useEffect(() => {
    load();
  }, [load]);

  const sections = useMemo(() => {
    if (!data) return [];
    const t = data.today;
    const tomorrow = addDays(t, 1);
    const week = addDays(t, 6);
    const S = [
      {
        key: 'overdue',
        ru: 'Просрочено',
        en: 'Overdue',
        tone: '#ef4444',
        test: (d) => d && d < t,
      },
      {
        key: 'today',
        ru: 'Сегодня',
        en: 'Today',
        tone: orchard.cherry,
        test: (d) => d === t,
      },
      {
        key: 'tomorrow',
        ru: 'Завтра',
        en: 'Tomorrow',
        tone: '#D98E04',
        test: (d) => d === tomorrow,
      },
      {
        key: 'week',
        ru: 'На этой неделе',
        en: 'This week',
        tone: '#3b82f6',
        test: (d) => d && d > tomorrow && d <= week,
      },
      {
        key: 'later',
        ru: 'Позже',
        en: 'Later',
        tone: '#64748b',
        test: (d) => d && d > week,
      },
      {
        key: 'nodate',
        ru: 'Без срока',
        en: 'No date',
        tone: '#94a3b8',
        test: (d) => !d,
      },
    ];
    return S.map((s) => ({
      ...s,
      items: data.items.filter((i) => s.test(i.date)),
    })).filter((s) => s.items.length);
  }, [data]);

  const finishTask = async (it) => {
    setDone((s) => new Set(s).add(it.id));
    await fetch(`/api/tasks/${it.id}`, {
      method: 'PATCH',
      credentials: 'include',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ status: 'done' }),
    }).catch(() => {});
  };

  if (error) return <Typography color="error">{error}</Typography>;
  if (!data) return <CircularProgress />;

  const total = data.items.length;
  let n = 0;

  return (
    <Box sx={{ maxWidth: 820 }}>
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          gap: 1.5,
          flexWrap: 'wrap',
          mb: 2,
        }}
      >
        <Typography sx={{ color: 'text.secondary', flex: 1, minWidth: 200 }}>
          {total
            ? en
              ? `${total} things — tasks, reports, plan checks, birthdays`
              : `Дел: ${total} — задачи, сдача отчётов, проверки по плану, дни рождения`
            : en
              ? 'Nothing due — enjoy!'
              : 'Ничего не горит 🎉'}
        </Typography>
        <ToggleButtonGroup
          size="small"
          exclusive
          value={days}
          onChange={(e, v) => v && setDays(v)}
        >
          <ToggleButton value={7}>{en ? 'week' : 'неделя'}</ToggleButton>
          <ToggleButton value={14}>{en ? '2 weeks' : '2 недели'}</ToggleButton>
          <ToggleButton value={31}>{en ? 'month' : 'месяц'}</ToggleButton>
        </ToggleButtonGroup>
        <Button size="small" onClick={onOpenBoard}>
          {en ? 'Task board →' : 'Доска задач →'}
        </Button>
      </Box>
      {sections.map((s) => (
        <Box key={s.key} sx={{ mb: 2.5 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
            <Box
              sx={{
                width: 10,
                height: 10,
                borderRadius: '50%',
                bgcolor: s.tone,
              }}
            />
            <Typography sx={{ fontWeight: 800 }}>
              {en ? s.en : s.ru} · {s.items.length}
            </Typography>
          </Box>
          <Box
            sx={{
              display: 'grid',
              gap: 0.75,
              pl: { xs: 0, sm: 2.5 },
              borderLeft: { sm: `2px solid ${alpha(s.tone, 0.25)}` },
            }}
          >
            {s.items.map((it) => {
              const K = KIND[it.kind] || KIND.task;
              const finished = done.has(it.id);
              n += 1;
              return (
                <ButtonBase
                  key={`${it.kind}${it.id}`}
                  onClick={() => navigate(it.url)}
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 1.25,
                    p: 1.1,
                    pl: 1.25,
                    textAlign: 'left',
                    borderRadius: 3,
                    bgcolor: 'background.paper',
                    border: '1px solid',
                    borderColor: 'divider',
                    opacity: finished ? 0.45 : 1,
                    animation: `${rise} 0.35s ease both`,
                    animationDelay: `${Math.min(n, 14) * 30}ms`,
                    transition:
                      'transform 0.18s ease, box-shadow 0.18s ease, opacity 0.3s ease',
                    '&:hover': {
                      transform: 'translateY(-1px)',
                      boxShadow: '0 6px 18px -12px rgba(0,0,0,0.35)',
                    },
                  }}
                >
                  <Box
                    sx={{
                      width: 34,
                      height: 34,
                      borderRadius: 2,
                      display: 'grid',
                      placeItems: 'center',
                      flexShrink: 0,
                      bgcolor: alpha(K.color, 0.12),
                      color: K.color,
                    }}
                  >
                    <K.Icon fontSize="small" />
                  </Box>
                  <Box sx={{ flex: 1, minWidth: 0 }}>
                    <Typography
                      noWrap
                      sx={{
                        fontWeight: 700,
                        fontSize: '0.92rem',
                        textDecoration: finished ? 'line-through' : 'none',
                      }}
                    >
                      {it.title}
                    </Typography>
                    <Typography
                      noWrap
                      sx={{ fontSize: '0.78rem', color: 'text.secondary' }}
                    >
                      {[
                        it.date && s.key !== 'today' && s.key !== 'tomorrow'
                          ? dateRu(it.date)
                          : '',
                        en ? K.en : K.ru,
                        it.subtitle,
                      ]
                        .filter(Boolean)
                        .join(' · ')}
                    </Typography>
                  </Box>
                  {it.kind === 'task' && (
                    <Tooltip title={en ? 'Done' : 'Сделано'}>
                      <Checkbox
                        checked={finished}
                        onClick={(e) => e.stopPropagation()}
                        onChange={() => !finished && finishTask(it)}
                        sx={{
                          color: K.color,
                          '&.Mui-checked': { color: orchard.leaf },
                        }}
                      />
                    </Tooltip>
                  )}
                </ButtonBase>
              );
            })}
          </Box>
        </Box>
      ))}
    </Box>
  );
}
